import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaHome, FaBookOpen, FaCalendarAlt, FaUser, FaCoins, FaUsers, FaUserFriends, FaCog, FaTrophy, FaMedal, FaSignOutAlt, FaSlidersH } from 'react-icons/fa';
import { AuthContext } from '../AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Player } from '../types';

const GAME_ROUTES = ['/lobby', '/game'];

interface NavBarProps {
  players: Player[];
}

// Hamburger menu toggle — a real button so it's keyboard- and screen-reader-usable
const MenuIcon = styled.button<{ $containerWidth: number; $containerWidthXL: number }>`
  background: none;
  border: none;
  padding: 0;
  color: rgb(255, 255, 255);
  font-size: 26px;
  cursor: pointer;
  position: fixed;
  top: calc(10px + env(safe-area-inset-top, 0px));
  /* Stay inside the AppContainer's right edge at any viewport width.
     Formula: (viewport - containerWidth) / 2 + padding, but at least 16px. */
  right: max(16px, calc((100vw - ${({ $containerWidth }) => $containerWidth}px) / 2 + 16px));
  z-index: 2000;
  width: 26px;
  height: 22.5px;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Track the wider app column on widescreen (must match AppContainer) */
  @media (min-width: 1440px) {
    right: max(16px, calc((100vw - ${({ $containerWidthXL }) => $containerWidthXL}px) / 2 + 16px));
  }
`;

// Sidebar with Figma specs
const Drawer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 240px;
  height: 100vh;
  background: rgb(0, 17, 26);
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
  z-index: 3000;
  padding: 20px;
`;

const DrawerMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  margin-top: 60px; /* Space for close button */
`;

const DrawerItem = styled.li`
  margin-bottom: 20px;
`;

// Menu item with Figma specs
const DrawerLink = styled(Link)`
  /* Figma menu item specs */
  width: 150px;
  height: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  color: rgb(255, 255, 255);
  text-decoration: none;
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 23px;
  text-transform: uppercase;
  gap: 12px;

  &:hover {
    color: rgb(0, 213, 255);
  }

  svg {
    font-size: 18px;
  }
`;

const DrawerLinkWithBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 150px;
  height: 40px;
`;

const Divider = styled.div`
  width: 150px;
  height: 1px;
  margin: 8px 0 12px 0;
  background: linear-gradient(90deg, rgba(255,255,255,0.9), rgba(255,255,255,0.25));
`;

const LogoutButton = styled.button`
  width: 150px;
  height: 40px;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.45);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 23px;
  text-transform: uppercase;
  gap: 12px;
  padding: 0;
  transition: color 0.2s;

  &:hover {
    color: #ff5252;
  }

  svg { font-size: 18px; }
`;

const Badge = styled.span`
  background-color: #ff5252;
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 12px;
  font-weight: bold;
  min-width: 18px;
  text-align: center;
`;

// Close button with white color — real button for keyboard/screen-reader access
const CloseBtn = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 24px;
  cursor: pointer;
  color: rgb(255, 255, 255);
  background: none;
  border: none;
  padding: 0;
  line-height: 0;
`;

const NavBar: React.FC<NavBarProps> = ({ players }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  const { isAuthenticated, user, getFriendRequests, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isWide = !GAME_ROUTES.some((r) => location.pathname === r || location.pathname.startsWith(r + '/'));
  const containerWidth = isWide ? 960 : 600;
  const containerWidthXL = isWide ? 1120 : 600; // AppContainer width at ≥1440px

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
    navigate('/');
  };

  const isGameInProgress = players.length > 0;
  const isOnGamePage = location.pathname === '/game';
  const isOnStartScreen = location.pathname === '/';
  // Hide the nav entirely on big-screen tournament view (it's a separate fullscreen display)
  const isScreenView = location.pathname.endsWith('/screen');

  // Load friend request count when user is authenticated
  useEffect(() => {
    const loadFriendRequestCount = async () => {
      if (isAuthenticated && user) {
        try {
          const requests = await getFriendRequests();
          setFriendRequestCount(requests.length);
        } catch (error) {
          console.error('Error loading friend requests:', error);
        }
      }
    };

    loadFriendRequestCount();
  }, [isAuthenticated, user, getFriendRequests]);

  if (isScreenView) return null;

  return (
    <>
      <MenuIcon
        $containerWidth={containerWidth}
        $containerWidthXL={containerWidthXL}
        onClick={toggleMenu}
        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isOpen}
      >
        {isOpen ? <FaTimes /> : <FaBars />}
      </MenuIcon>
      <AnimatePresence>
        {isOpen && (
          <Drawer
            initial={{ x: '100%' }}
            animate={{ x: '0%' }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3 }}
          >
            <CloseBtn onClick={toggleMenu} aria-label="Close navigation menu">
              <FaTimes />
            </CloseBtn>
            <DrawerMenu>
              {isGameInProgress && !isOnGamePage && (
                <DrawerItem>
                  <DrawerLink to="/game" onClick={toggleMenu}>
                    <FaHome />
                    Back to Game
                  </DrawerLink>
                </DrawerItem>
              )}
              {!isGameInProgress && !isOnStartScreen && (
                <DrawerItem>
                  <DrawerLink to="/" onClick={toggleMenu}>
                    <FaHome />
                    Home
                  </DrawerLink>
                </DrawerItem>
              )}
              <DrawerItem>
                <DrawerLink to="/rules" onClick={toggleMenu}>
                  <FaBookOpen />
                  Rules
                </DrawerLink>
              </DrawerItem>
              <DrawerItem>
                <DrawerLink to="/about" onClick={toggleMenu}>
                  <FaBookOpen />
                  About
                </DrawerLink>
              </DrawerItem>
              <DrawerItem>
                <DrawerLink to="/buy" onClick={toggleMenu}>
                  <FaCoins />
                  Purchase
                </DrawerLink>
              </DrawerItem>
              <DrawerItem>
                <DrawerLink to="/events" onClick={toggleMenu}>
                  <FaCalendarAlt />
                  Events
                </DrawerLink>
              </DrawerItem>
              <DrawerItem>
                <DrawerLink to="/tournaments" onClick={toggleMenu}>
                  <FaTrophy />
                  Tournaments
                </DrawerLink>
              </DrawerItem>
              <DrawerItem>
                <DrawerLink to="/leagues" onClick={toggleMenu}>
                  <FaMedal />
                  Leagues
                </DrawerLink>
              </DrawerItem>
              {isAuthenticated ? (
                <>
                  <DrawerItem>
                    <DrawerLink to="/profile" onClick={toggleMenu}>
                      <FaUser />
                      Profile
                    </DrawerLink>
                  </DrawerItem>
                  <DrawerItem>
                    <DrawerLink to="/friends" onClick={toggleMenu}>
                      <FaUsers />
                      Friends
                    </DrawerLink>
                  </DrawerItem>
                  <DrawerItem>
                    <DrawerLinkWithBadge>
                      <DrawerLink to="/friend-requests" onClick={toggleMenu}>
                        <FaUserFriends />
                        Friend Requests
                      </DrawerLink>
                      {friendRequestCount > 0 && (
                        <Badge>{friendRequestCount}</Badge>
                      )}
                    </DrawerLinkWithBadge>
                  </DrawerItem>
                  {user?.role === 'admin' && (
                    <DrawerItem>
                      <DrawerLink to="/admin" onClick={toggleMenu}>
                        <FaCog />
                        Admin Panel
                      </DrawerLink>
                    </DrawerItem>
                  )}
                  <DrawerItem>
                    <LogoutButton onClick={handleLogout}>
                      <FaSignOutAlt />
                      Log Out
                    </LogoutButton>
                  </DrawerItem>
                </>
              ) : (
                <>
                  <DrawerItem>
                    <DrawerLink to="/login" onClick={toggleMenu}>
                      <FaUser />
                      Login
                    </DrawerLink>
                  </DrawerItem>
                </>
              )}
              <DrawerItem>
                <Divider />
              </DrawerItem>
              <DrawerItem>
                <DrawerLink to="/settings" onClick={toggleMenu}>
                  <FaSlidersH />
                  Settings
                </DrawerLink>
              </DrawerItem>
              {/* AdSense unit removed: ads inside a navigation menu violate
                  "Google-served ads on screens used for navigation". */}
            </DrawerMenu>
          </Drawer>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavBar;
